import { useState, type FC, useRef } from 'react'

import { Tooltip } from '@skytech/ui'
import { TreeNode } from 'react-organizational-chart'
import { useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { useOnClickOutside } from '@/hooks/useOnClickOutside'
import { APP_ROUTES } from '@/util/app-routes'

import { setMultipleDrillDownAction } from '../context/actions.context'
import { useDrillDownContext } from '../context/index.context'
import type { CustomerHeadData } from '../types'

import { Amount } from './Amount'
import { Customer } from './Customer'
import { LinkComponent } from './Link'
import { Menu } from './Menu'
import { RootNode } from './RootNode'

import type { DrillDownType } from '../../resources'

export const CustomerHead: FC<{
  customerHead: CustomerHeadData
  isRootNode?: boolean
}> = ({ customerHead, isRootNode = false }) => {
  const customerList = Array.from(customerHead.customers.values())

  const CustomerList = (
    <div className="customer-list">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(12rem,1fr))]">
        {customerList.map(customer => (
          <Customer key={customer.id} customer={customer} />
        ))}
      </div>
    </div>
  )

  if (isRootNode) {
    return (
      <RootNode label={<Label customerHead={customerHead} />}>
        <TreeNode label={CustomerList} />
      </RootNode>
    )
  }

  return (
    <TreeNode label={<Label customerHead={customerHead} />}>
      <TreeNode label={CustomerList} />
    </TreeNode>
  )
}

const Label: FC<{ customerHead: CustomerHeadData }> = ({ customerHead }) => {
  const [displayMenu, setDisplayMenu] = useState(false)
  const menuRef = useRef(null)

  useOnClickOutside(menuRef, () => displayMenu && onCurrencyButtonClick(''))

  const { state, dispatch } = useDrillDownContext()

  const navigate = useNavigate()

  const onClick = (pathname: string) => {
    const params = {
      type: 'customer-head' as DrillDownType,
      typeId: String(customerHead.id),
      period: String(state.period),
      year: String(state.year),
      currency: state.currency,
    }

    const search = new URLSearchParams(params).toString()

    dispatch(setMultipleDrillDownAction({ ...params }))

    navigate({ pathname, search })
  }

  const onCurrencyButtonClick = (
    currency: string,
    e?: React.KeyboardEvent<HTMLSpanElement> | React.MouseEvent<HTMLSpanElement>
  ) => {
    // Only proceed if it's a mouse click or Enter key press
    if (e?.type === 'keydown' && (e as React.KeyboardEvent).key !== 'Enter') return

    const newCurrency = currency && currency !== state.currency ? currency : ''
    const newDisplayMenu = Boolean(newCurrency)

    dispatch(setMultipleDrillDownAction({ currency: newCurrency }))
    setDisplayMenu(newDisplayMenu)

    e?.stopPropagation()
  }

  const amounts = Array.from(customerHead.values.entries()).map(([currency, value]) => ({
    currency,
    value,
  }))

  return (
    <button
      ref={menuRef}
      className={twJoin(
        'tree-node customer-head cursor-pointer border-none transition-all duration-[250ms] ease-out hover:scale-110',
        displayMenu && 'scale-[106%]'
      )}
      onClick={e => {
        e.stopPropagation()
        const firsCurrency = amounts[0].currency
        onCurrencyButtonClick(firsCurrency)
      }}
    >
      <div className="flex h-min min-h-full w-full max-w-full cursor-pointer flex-col rounded-[4px] border-none">
        <div className="max-w-full">
          <span
            data-tooltip-id={String(customerHead.id)}
            data-tooltip-content={String(customerHead.name)}
            className="mb-[0.2em] block overflow-hidden text-ellipsis whitespace-nowrap text-[0.8em] font-normal"
            data-event="click focus"
          >
            <Tooltip id={String(customerHead.id)} style={{ zIndex: 900 }} className="break-all" />
            {customerHead.name}
          </span>
          <div className={`mt-[0.1em] ${amounts.length > 1 ? 'text-[1.1rem]' : 'text-[1.2rem]'} `}>
            {amounts.map(({ value, currency }, index) => (
              <Amount
                key={currency}
                amount={value}
                currency={currency}
                onClick={onCurrencyButtonClick}
                isActive={state.currency === currency && displayMenu}
                rotateColors
                tabIndex={index}
              />
            ))}
          </div>
        </div>
      </div>
      <Menu selected={displayMenu}>
        <LinkComponent
          onClick={() => onClick(`${APP_ROUTES.REPORTS_DRILL_DOWN_SERVICE}`)}
          label="Services"
        />
        <LinkComponent
          onClick={() => onClick(`${APP_ROUTES.REPORTS_DRILL_DOWN_DEPARTMENT}`)}
          label="Dept. / Cost Center"
        />
        <LinkComponent
          onClick={() => onClick(`${APP_ROUTES.REPORTS_DRILL_DOWN_COST_OBJECT}`)}
          label="Number / Cost Item"
        />
      </Menu>
    </button>
  )
}
